import { observer } from "mobx-react-lite";
import {
  AutoComplete,
  Button,
  DatePicker,
  Flex,
  Form,
  FormProps,
  Input,
  InputNumber,
  Select,
  Spin,
  Tag,
  Upload,
  UploadFile,
  message,
  Checkbox,
} from "antd";
import Layout from "../../components/layout/Layout";
import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { UploadChangeParam } from "antd/es/upload";
import { UploadOutlined, SaveOutlined, PlusOutlined } from "@ant-design/icons";
import { useStore } from "../../stores/root-store-context";
import TextArea from "antd/es/input/TextArea";
import {
  CloseOutlined,
} from "@ant-design/icons";
import ImgCrop from "antd-img-crop";
import { useTranslation } from "react-i18next";
import { SkillLevelName, SkillLevelNameColor } from "../../helpers/skills";
import dayjs from "dayjs";
import EducationBlock from "../../components/blocks/EducationBlock";
import { AddEducation } from "./AddEducation";
import { AddJobTitle } from "./AddJobTitle";
import ExperienceBlock from "../../components/blocks/ExperienceBlock";
import { AddExperience } from "./AddExperience";
import { AddSkill } from "../../components/skill/AddSkill";
import 'react-phone-number-input/style.css';
import PhoneInput from 'react-phone-number-input';
import { parsePhoneNumberFromString } from 'libphonenumber-js/max';
import i18n from "../../i18n";
import { Education } from "../../models/Education";
import { Skill } from "../../models/Skill";
import { Employee } from "../../models/Employee";

const EditEmployee = observer(() => {
  // Каталог скілів: ESCO API (Єврокомісія) — безкоштовний, авторизація не потрібна
  const SKILLS_API_URL = "https://ec.europa.eu/esco/api/search";

  const navigate = useNavigate();
  const { applicationStore, employeesStore, educationStore } = useStore();
  const [messageApi] = message.useMessage();
  const { t } = useTranslation();
  const uploadRef = useRef<HTMLInputElement>(null);
  const [autoCompleteSkillValue, setAutoCompleteSkillValue] = useState<string>('');
  const { id } = useParams();
  const [form] = Form.useForm();
  const [skillForm] = Form.useForm();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalJobTitle, setIsModalJobTitle] = useState(false);
  const [isModalExperience, setIsModalExperience] = useState(false);
  const [educationList, setEducationList] = useState<Education[]>([]);
  const [experienceList, setExperienceList] = useState<any>([]);
  const [selectedSkill, setSelectedSkill] = useState<Skill>();
  const [searchText, setSearchText] = useState<string>("");
  const [isModalSkillVisible, setIsModalSkillVisible] = useState(false);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [filteredSkills, setFlteredSkills] = useState<any[]>([]);
  const [isEditing, setIsEditing] = useState(false);

  //#region Utilities
  const langToCountry = (lang?: string): any => {
    if (!lang) return 'UA';
    const m: Record<string, string> = {
      uk: 'UA',
      ru: 'RU',
      en: 'US',
    };
    return (m[lang.toLowerCase()] || 'UA') as any;
  };

  function ParseToJson(str: string): any[] {
    try {
      return JSON.parse(str);
    } catch (e) {
      return [];
    }
  }

  function updatePage() {
    if (id) {
      employeesStore.getUserById(id).then((result) => {
        if (!isEditing) {
          form.setFieldsValue(result);
        }
        fetchExperience();
        fetchEducation();
        if (result?.photoId) {
          setFileList([
            {
              uid: "-1",
              name: `${result?.photoId}`,
              status: "done",
              url: `${applicationStore?.siteConfig?.baseCloudinary}/image/upload/${result?.photoId}`,
            },
          ]);
        }
      });
    } else {
      navigate("/404");
    }
  }

  const fileChange = (info: UploadChangeParam) => {
    let fileList = [...info.fileList];

    // Limit to one file
    fileList = fileList.slice(-1);

    // Only show the last file
    setFileList(fileList);

    if (info.file.status === "done") {
      message.success(`${info.file.name} ${t("UploadSuccess")}`);
    } else if (info.file.status === "error") {
      message.error(`${info.file.name} ${t("UploadFailed")}`);
    }
  };

  const handleSearch = (value: string) => {
    setSearchText(value);
  };

  const showModal = () => {
    setIsModalOpen(true);
  };

  const hideModal = () => {
    setIsModalOpen(false);
  };
  //#endregion


  //#region Job Title
  const showModalJobTitle = () => {
    setIsModalJobTitle(true);
  };

  const hideModalJobTitle = () => {
    setIsModalJobTitle(false);
  };
  //#endregion


  //#region Experience
  const showModalExperience = () => {
    setIsModalExperience(true);
  };

  const hideModalExperience = () => {
    setIsModalExperience(false);
  };

  const removeExperience = async (experienceToRemove: any) => {
    var result = await employeesStore.removeExperience(
      experienceToRemove.Company,
      experienceToRemove.Project,
      experienceToRemove.Position,
      experienceToRemove.Description,
      experienceToRemove.StartDate,
      experienceToRemove.EndDate
    );
    if (result) {
      message.success(t("Success"));
      setExperienceList((prev: any) =>
        prev.filter(
          (item: any) =>
            !(
              item.Company === experienceToRemove.Company &&
              item.Project === experienceToRemove.Project &&
              item.Position === experienceToRemove.Position &&
              item.Description === experienceToRemove.Description &&
              item.StartDate === experienceToRemove.StartDate &&
              item.EndDate === experienceToRemove.EndDate
            )
        )
      );
    }
    else {
      message.error(t("SomethingWentWrong"));
    }
  };

  const fetchExperience = async () => {
    const data = ParseToJson(employeesStore.user?.experience);
    const experienceArray = Array.isArray(data) ? data : [];
    setExperienceList(experienceArray);
  }
  //#endregion


  //#region Education
  const fetchEducation = async () => {
    if (id) {
      await employeesStore.getUserById(id);
    }
    if (employeesStore.user?.education) {
      const data = ParseToJson(employeesStore.user?.education);
      const educationArray = Array.isArray(data) ? data : [];
      setEducationList(educationArray);
    }
  }

  const removeEducation = async (educationToRemove: Education) => {
    var result = await employeesStore.ApiStore.CommonApiClient.removeEducation(
      educationToRemove.Country,
      educationToRemove.City,
      educationToRemove.University,
      educationToRemove.Profession,
      educationToRemove.Degree,
      educationToRemove.StartDate,
      educationToRemove.EndDate
    );
    if (result) {
      messageApi.success(t("Success"));
      setEducationList((prev) =>
        prev.filter(
          (item) =>
            !(
              item.Country === educationToRemove.Country &&
              item.City === educationToRemove.City &&
              item.University === educationToRemove.University &&
              item.Profession === educationToRemove.Profession &&
              item.Degree === educationToRemove.Degree &&
              item.StartDate === educationToRemove.StartDate &&
              item.EndDate === educationToRemove.EndDate
            )
        )
      );
    } else {
      messageApi.error(t("SomethingWentWrong"));
    }
  };
  //#endregion


  //#region Skills
  const handleSkillChange = async (skillId: string, title: string) => {
    setSelectedSkill({
      skillId: skillId,
      title: title,
      level: undefined,
      experience: undefined,
    });
    setAutoCompleteSkillValue(title);
  };

  async function updateSkills() {
    const updatedSkills = await employeesStore.ApiStore.CommonApiClient.getSkills();
    applicationStore.skills = updatedSkills;
  }

  const removeSkill = async (skillId: string) => {
    message.open({
      type: "loading",
      content: t("Removing"),
      key: "remove",
    });
    const result =
      await employeesStore.ApiStore.CommonApiClient.RemoveSkillFromUser(
        parseInt(skillId),
        parseInt(id as string) as number
      );
    message.destroy("remove");
    if (result) {
      message.success(t("RemoveSuccess"));
      employeesStore.setUser({
        ...employeesStore.user,
        userSkills: employeesStore.user?.userSkills?.filter(
          (skill) => skill.skill.skillId !== skillId
        ),
      });
    } else message.error("Removing failed");
  };

  const addSkill = async () => {
    messageApi.open({
      type: "loading",
      content: t("Saving"),
      key: "saving",
    });
    if (
      selectedSkill?.skillId === undefined ||
      selectedSkill?.title === undefined ||
      selectedSkill?.experience === undefined ||
      selectedSkill?.level === undefined
    ) {
      messageApi.destroy("saving");
      messageApi.error("Saving failed! Fill all necessary fields");
      return;
    }
    const result =
      await employeesStore.ApiStore.CommonApiClient.AssignSkillToUser(
        selectedSkill?.skillId,
        parseInt(id as string) as number,
        selectedSkill?.level as number,
        selectedSkill?.experience as number
      );
    messageApi.destroy("saving");
    if (result) {
      message.success(t("SaveSuccess"));
      const userSkill = {
        skill: {
          skillId: String(selectedSkill?.skillId) as string,
          title: selectedSkill?.title as string,
        },
        skillLevel: String(selectedSkill?.level) as string,
        experienceYears: String(selectedSkill?.experience) as string,
        confirmationDate: "",
      };
      employeesStore.setUser({
        ...employeesStore.user,
        userSkills: [...employeesStore.user?.userSkills, userSkill],
      });
      setAutoCompleteSkillValue('');
      setSearchText('');
    } else message.error("Saving failed");
    skillForm.resetFields();
    setSelectedSkill(undefined);
  };
  //#endregion


  //#region Form Finish
  const onFinish: FormProps<Employee>["onFinish"] = async (
    newUser: Employee
  ) => {
    newUser.id = employeesStore.user.id;
    message.open({
      type: "loading",
      content: t("Saving"),
      key: "saving",
    });

    if (fileList.length > 0 && fileList[0].uid !== "-1") {
      const photo = await employeesStore.uploadPhoto(fileList[0], employeesStore.user.id);
      newUser.photoId = photo.publicId;
    }

    try {
      await employeesStore.editUser(newUser);

      const updatedUser = await employeesStore.getUserById(newUser.id);

      form.setFieldsValue(updatedUser);
      message.success(t("SaveSuccess"));
    } catch (error) {
      message.error(t("SaveFailed"));
    } finally {
      message.destroy("saving");
      setIsEditing(false);
    }
  };
  //#endregion


  //#region Effects
  useEffect(() => {
    updatePage()
  }, [id]);

  useEffect(() => {
    if (employeesStore.user?.education) {
      const data = ParseToJson(employeesStore.user?.education);
      const educationArray = Array.isArray(data) ? data : [data];
      setEducationList(educationArray);
    }
  }, [employeesStore.user?.education])

  useEffect(() => {
    if (employeesStore.user) {
      fetchExperience();
      fetchEducation();
    }
  }, []);

  useEffect(() => {
    console.log("useEffect");
    console.log(JSON.parse(JSON.stringify(employeesStore.user?.userSkills)));
    const newSkill = applicationStore.skills
      ?.filter(
        (el) =>
          el.title &&
          el.title.toLowerCase().includes(searchText.toLowerCase()) &&
          !employeesStore.user.userSkills?.some(
            (skill) => skill.skill.skillId == el.skillId
          )
      )
      .map((el) => ({
        value: el.title,
        label: el.title,
        skillId: el.skillId,
      }));
    console.log(newSkill);

    setFlteredSkills(newSkill)
  }, [employeesStore.user.userSkills.length, searchText, applicationStore.skills, employeesStore.user.userSkills]);

  useEffect(() => {
    if (!isEditing && employeesStore.user) {
      form.setFieldsValue(employeesStore.user);
    }
  }, [employeesStore.user, isEditing]);
  //#endregion

  return (
    <Layout headerStyle={1} footerStyle={2}>
      <div>
        <section className="contact-section fix section-padding">
          <div className="container" style={{ maxWidth: 800 }}>
            <Button onClick={() => {
              navigate(-1)
            }}>{t("Back")}</Button>
            <Spin spinning={employeesStore.isLoading}>

              <Form
                form={form}
                onFinish={onFinish}
                onValuesChange={() => setIsEditing(true)}
                layout="vertical"
              >
                <Form.Item<Employee>
                  style={{ width: "100%" }}
                  rules={[{ required: true }]}
                >
                  <Flex justify="center" vertical align="center">
                    <ImgCrop rotationSlider>
                      <Upload
                        className="avatar-uploader"
                        ref={uploadRef}
                        style={{ alignSelf: "center" }}
                        accept="image/*"
                        maxCount={1}
                        listType="picture-circle"
                        fileList={fileList}
                        onChange={fileChange}
                        beforeUpload={() => false}
                      >
                        {fileList.length === 0 && (
                          <Button icon={<UploadOutlined />}></Button>
                        )}
                      </Upload>
                    </ImgCrop>
                    {fileList.length > 0 && (
                      <Button style={{ marginTop: 10 }} icon={<UploadOutlined />} onClick={() => {
                        if (uploadRef.current) {
                          const input = document.querySelector('.avatar-uploader input[type="file"]') as HTMLElement;
                          if (input) {
                            input.click();
                          }
                        }
                      }}>{t('Upload')}</Button>
                    )}
                  </Flex>
                </Form.Item>

                <Form.Item<Employee>
                  label={`${t("user.Name")}`}
                  name="firstName"
                  style={{ width: "100%" }}
                  rules={[{ required: true }]}
                >
                  <Input placeholder={`${t("user.Name")}`} />
                </Form.Item>

                <Form.Item<Employee>
                  label={`${t("user.Surname")}`}
                  name="lastName"
                  style={{ width: "100%" }}
                  rules={[{ required: true }]}
                >
                  <Input placeholder={`${t("user.Surname")}`} />
                </Form.Item>

                <Form.Item<Employee>
                  label={t('PhoneNumber')}
                  name="phoneNumber"
                  validateTrigger={['onChange', 'onBlur']}
                  rules={[
                    { required: true, message: t('PleaseEnterPhoneNumber') },
                    {
                      validator: async (_, value?: string) => {
                        if (!value) return;

                        const parsed = parsePhoneNumberFromString(value);
                        if (!parsed || !parsed.isValid()) {
                          throw new Error(t('PhoneNumberFormat'));
                        }

                        const digits = value.replace(/\D/g, '');
                        if (value.startsWith('+380') && digits.length !== 12) {
                          throw new Error(t('PhoneNumberFormat'));
                        }
                      },
                    },
                  ]}
                >
                  <PhoneInput
                    className="my-phone-input"
                    international
                    defaultCountry={langToCountry(i18n.language)}
                    countryCallingCodeEditable={false}
                    placeholder={t('PhoneNumber')}
                    value={form.getFieldValue('phoneNumber')}
                    onChange={(val) => form.setFieldValue('phoneNumber', val)}
                  />
                </Form.Item>

                <Form.Item<Employee>
                  label={t("HidePhoneNumber")}
                  name="isPhoneNumberPrivate"
                  valuePropName="checked"
                  style={{ width: "100%" }}
                >
                  <Checkbox>{t("HidePhoneNumberFromOthers")}</Checkbox>
                </Form.Item>

                <Form.Item<Employee>
                  label="Skype"
                  name="skype"
                  style={{ width: "100%" }}
                  rules={[{ required: false }]}
                >
                  <Input placeholder="Skype" />
                </Form.Item>

                {/* <Form.Item<Employee>
                  label="Viber"
                  name="viber"
                  style={{ width: "100%" }}
                  rules={[{ required: false }]}
                >
                  <Input placeholder="Skype" />
                </Form.Item> */}

                <Form.Item<Employee>
                  label="Telegram"
                  name="telegram"
                  style={{ width: "100%" }}
                  rules={[{ required: false }]}
                >
                  <Input placeholder="Telegram" />
                </Form.Item>

                <Form.Item<Employee>
                  label={`${t("City")}`}
                  name="city"
                  style={{ width: "100%" }}
                  rules={[{ required: false }]}
                >
                  <Input placeholder={`${t("City")}`} />
                </Form.Item>

                <Form.Item<Employee> name="jobTitleId" hidden>
                  <Input />
                </Form.Item>

                <Form.Item<Employee>
                  label={`${t("user.JobTitle")}`}
                  name="jobTitle"
                  style={{ width: "100%" }}
                  rules={[{ required: false }]}
                >
                  <AutoComplete
                    placeholder={`${t("user.JobTitle")}`}
                    options={[
                      ...(employeesStore.jobTitles
                        ? employeesStore.jobTitles
                          .slice()
                          .sort((a, b) => a.title.localeCompare(b.title, 'uk'))
                          .map((el, index) => ({
                            key: `${el.id}-${index}`,
                            value: el.title,
                            label: el.title,
                            data: el,
                          }))
                        : []),
                      {
                        key: "create-job-title",
                        value: "create-job-title",
                        label: <Button onClick={showModalJobTitle}>{t("CreateJobTitle")}</Button>,
                      },
                    ]}

                    onChange={(value, option: any) => {
                      if (option?.value && option?.value !== "create-job-title") {
                        console.log("option");
                        console.log(option);
                        console.log(value);
                        form.setFieldValue("jobTitleId", option.data.id)
                        form.setFieldValue("jobTitle", option.value)
                        console.log(option);
                      } else {
                        console.log(value);
                      }
                    }}
                  />
                </Form.Item>

                <Form.Item<Employee>
                  label={`${t("user.BirthDate")}`}
                  labelCol={{ span: 12 }}
                  name="birthDay"
                  style={{ width: "100%" }}
                  rules={[{ required: false }]}
                  getValueFromEvent={(date) => date ? dayjs(date).format('YYYY-MM-DD') : undefined}
                  getValueProps={(value) => ({ value: value ? dayjs(value, 'YYYY-MM-DD') : null })}
                >
                  <DatePicker />
                </Form.Item>

                <Form.Item<Employee>
                  label={`${t("user.AboutMe")}`}
                  name="aboutMe"
                  style={{ width: "100%" }}
                  rules={[{ required: false }]}
                >
                  <TextArea placeholder={t("user.AboutMe")} rows={4} autoSize />
                </Form.Item>

                <Form.Item<Employee>
                  label={`${t("user.MyHobby")}`}
                  name="hobby"
                  style={{ width: "100%" }}
                  rules={[{ required: false }]}
                >
                  <TextArea placeholder={t("user.MyHobby")} rows={4} autoSize />
                </Form.Item>

                <Form.Item<Employee>
                  // label={`${t("user.MySkills")}`}
                  style={{
                    width: "100%", border: "1px solid #cecece", borderRadius: 5, padding: 5,
                    display: "flex", flexDirection: "column",
                    backgroundColor: "#fafafa"
                  }}
                  rules={[{ required: false }]}
                >
                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                    <div style={{ marginBottom: "15px", flex: "1 1 200px" }}>
                      <label style={{ marginBottom: 8 }}>{t("user.MySkills")}</label>
                      <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                        <AutoComplete
                          value={autoCompleteSkillValue}
                          showSearch={true}
                          placeholder={`${t("user.MySkills")}`}
                          options={[
                            ...filteredSkills.slice()
                              .sort((a, b) => a.label.localeCompare(b.label, 'uk')),
                          ].filter((el) => employeesStore.user?.userSkills.some(userSkill => userSkill.skill.skillId === el.skillId) === false)}
                          onSearch={handleSearch}
                          onSelect={(value: string, option: any) => {
                            const selected = applicationStore.skills.find((el) => el.title === value);
                            if (selected?.skillId) {
                              handleSkillChange(selected.skillId, value);
                            }
                          }}
                          onChange={(value) => {
                            setSearchText(value || "");
                            setAutoCompleteSkillValue(value || "");
                          }}
                          style={{ width: '100%' }}
                        />
                        <PlusOutlined
                          style={{
                            position: 'absolute',
                            right: 10,
                            cursor: 'pointer',
                            fontSize: 16,
                            color: '#1890ff'
                          }}
                          onClick={() => setIsModalSkillVisible(true)}
                        />
                      </div>
                    </div>

                    {selectedSkill?.skillId !== undefined && (
                      <Form key="skillForm" style={{ flex: "4 1 200px", display: "flex", gap: 10 }} form={skillForm} onFinish={addSkill}>
                        <Form.Item<any>
                          label={t("Level")}
                          name={t("Level")}
                          style={{ width: "100%", marginBottom: "46px" }}
                          rules={[{ required: false }]}
                        >
                          <Select
                            value={selectedSkill?.level}
                            onChange={(e) =>
                              setSelectedSkill({ ...selectedSkill, level: e })
                            }
                            options={[
                              { value: '1', label: t("Elementary"), key: '1' },
                              { value: '2', label: t("Intermediate"), key: '2' },
                              { value: '3', label: t("Advanced"), key: '3' },
                              { value: '4', label: t("Professional"), key: '4' },
                              { value: '5', label: t("Expert"), key: '5' },
                            ]}
                          />
                        </Form.Item>
                        <Form.Item<any>
                          label={t("SkillExperience")}
                          name="Skill experience"
                          style={{ width: "100%" }}
                          rules={[{ required: false }]}
                        >
                          <InputNumber
                            max={20}
                            min={0}
                            value={selectedSkill?.experience}
                            onChange={(e) =>
                              setSelectedSkill({
                                ...selectedSkill,
                                experience: e?.valueOf(),
                              })
                            }
                            placeholder={t("years")}
                          />
                        </Form.Item>
                        <div
                          key={"submitExpirence"}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Button
                            disabled={selectedSkill?.level === undefined || selectedSkill?.experience === undefined}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                            onClick={addSkill}
                            className="theme-btn"
                          >
                            {`${t("Add")}`}
                          </Button>
                        </div>
                      </Form>
                    )}

                    <Flex gap={10} wrap style={{ width: "100%" }}>
                      {employeesStore.user?.userSkills?.map((item, index) => {
                        return (
                          <Tag
                            key={index}
                            color="blue"
                            style={{
                              cursor: "pointer",
                              backgroundColor:
                                SkillLevelNameColor[parseInt(item.skillLevel)],
                            }}
                            onClick={() => {
                              removeSkill(item.skill.skillId);
                            }}
                          >
                            <div>
                              {item.skill.title} <CloseOutlined />
                            </div>
                            <div>{SkillLevelName[parseInt(item.skillLevel)]}</div>
                            <div>{[parseInt(item.experienceYears) || 0]} {t('years')}</div>
                          </Tag>
                        );
                      })}
                    </Flex>
                  </div>
                </Form.Item>

                <Form.Item<Employee>
                  name="education"
                  style={{ width: "100%" }}
                  rules={[{ required: false }]}
                >
                  <EducationBlock
                    educationList={educationList}
                    removeEducation={removeEducation}
                    canRemove={true}
                    updatePage={() => {
                      updatePage();
                      // fetchEducation();
                    }}
                  ></EducationBlock>
                  <Button style={{ marginBottom: 20 }} onClick={showModal}>{`${t("AddEducation")}`}</Button>
                </Form.Item>

                <Flex style={{ display: "block" }}>
                  <ExperienceBlock
                    experienceList={experienceList}
                    canRemove={true}
                    removeExperience={removeExperience}
                    updatePage={updatePage}
                  ></ExperienceBlock>
                  <Button onClick={showModalExperience}>{t('AddExperience')}</Button>
                </Flex>

                <Form.Item>
                  <Button
                    type="primary"
                    className="theme-btn"
                    htmlType="submit"
                    style={{ height: 60, display: "flex", float: "right" }}
                  >
                    {`${t("Save")}`}{" "}
                    <SaveOutlined style={{ fontSize: 20, margin: 5 }} />
                  </Button>
                </Form.Item>
              </Form>
            </Spin>
          </div>
        </section>
      </div>

      <AddSkill
        visible={isModalSkillVisible}
        onCancel={() => setIsModalSkillVisible(false)}
        onSkillSelect={handleSkillChange}
        SKILLS_API_URL={SKILLS_API_URL}
        existingSkills={applicationStore.skills}
      />

      <AddEducation
        isModalOpen={isModalOpen}
        hideModal={hideModal}
        userId={id}
        fetchEducation={updatePage}
        educationItem={undefined}
      />

      <AddJobTitle
        isModalOpen={isModalJobTitle}
        hideModal={hideModalJobTitle}
        userId={id}
      />

      <AddExperience
        key={"mainAddExperience"}
        isModalOpen={isModalExperience}
        hideModal={hideModalExperience}
        fetchExperience={() => {
          updatePage();
        }}
        experienceItem={undefined}
      />

    </Layout>
  );
});
export default EditEmployee;
